import React, { useEffect, useState, useRef, useCallback } from "react";
import { ReportComponent } from "../../services/types/ComponentTypes";
import { apiService } from "../../services/api";
import { useAppSelector, useAppDispatch } from "../../state/hooks";
import { fetchSubModuleRawData } from "../../state/slices/lookupsSlice";
import { getParentIdFromFormData } from "../../state/utils/configUtils";



interface ReportProps {
  config: ReportComponent;
  formData?: Record<string, any>;
  parentFieldLookupName?: string;
}

export const Report: React.FC<ReportProps> = ({
  config,
  formData = {},
  parentFieldLookupName
}) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [parentRecordData, setParentRecordData] = useState<Record<string, any> | null>(null);
  const previousParentIdRef = useRef<string | number | null>(null);

  const settings = config.Settings || config.settings;
  const parentField = config.Parent_Field || config.parentField;
  const includeLabel = config.IncludeLabel !== false;
  const label = config.label || config.Label || settings || "Report";

  // Get lookups state to access rawData
  const lookupsState = useAppSelector((state) => state.lookups);
  const dispatch = useAppDispatch();

  // Use the general helper function from configUtils
  const getParentId = useCallback((formData: Record<string, any>, parentField: string | undefined): string | number | null => {


    // Call the imported utility function, not itself!
    const parentId = getParentIdFromFormData(formData, parentField);



    return parentId;
  }, []);

  // Watch parent field changes and get the full selected record
  // Priority: 1. Check for direct selectedData from SearchableDropdown onChange
  //           2. Get from lookup rawData
  //           3. Fetch from API
  useEffect(() => {
    if (!parentField) {
      return;
    }

    const parentId = getParentId(formData, parentField);
    const selectedDataKey = `${parentField}_selectedData`;
    const selectedData = formData[selectedDataKey]; // Full record passed from SearchableDropdown onChange

    // Also check selectedData for variations
    let selectedDataFromVariations = null;
    if (!selectedData) {
      const selectedDataVariations = [
        `${parentField}_selectedData`,
        `parent_id_selectedData`,
        `parentid_selectedData`,
        `parentId_selectedData`,
      ];

      for (const variation of selectedDataVariations) {
        if (formData[variation] && typeof formData[variation] === "object") {
          selectedDataFromVariations = formData[variation];

          break;
        }
      }
    }

    const finalSelectedData = selectedData || selectedDataFromVariations;

    // If parentId hasn't changed, don't update
    if (parentId === previousParentIdRef.current) {
      return;
    }

    previousParentIdRef.current = parentId;

    // If no parentId, clear the record data
    if (!parentId || parentId === "" || parentId === null || parentId === undefined) {
      setParentRecordData(null);
      return;
    }

    // Priority 1: Use selectedData if available (from SearchableDropdown onChange)
    if (finalSelectedData && typeof finalSelectedData === "object") {
      // Validate that selectedData is a record object, not a config structure
      if (finalSelectedData.ControlType || finalSelectedData.ChildControls || finalSelectedData.FormName) {

        // Don't use it, fall through to other methods
      } else {

        setParentRecordData(finalSelectedData);
        return;
      }
    }

    // Priority 2: Get from lookup rawData
    if (parentFieldLookupName) {
      const lookupKey = parentFieldLookupName;
      let rawData = lookupsState.rawData[lookupKey] || [];

      // If rawData is not available, try to fetch it
      if (rawData.length === 0 && !lookupsState.loading[lookupKey]) {

        // Fetch rawData for this lookup
        dispatch(
          fetchSubModuleRawData({
            lookupName: parentFieldLookupName,
            parentValue: "", // Independent lookup
            lookuptype: "Select",
          })
        );
        return; // Will re-run when rawData is available
      }

      // Find the record matching the parentId
      const selectedRecord = rawData.find((item: any) => {
        // Try multiple ID fields that might match
        return (
          item.id === parentId ||
          String(item.id) === String(parentId) ||
          item.value === parentId ||
          String(item.value) === String(parentId) ||
          (item.parent_id !== undefined && item.parent_id === parentId) ||
          (item.parent_id !== undefined && String(item.parent_id) === String(parentId))
        );
      });

      if (selectedRecord) {

        setParentRecordData(selectedRecord);
        return;
      }
    }

    // Priority 3: Will fallback to fetching in fetchReportData

    setParentRecordData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentField, parentFieldLookupName, lookupsState.rawData, dispatch,
    // Only watch specific parent ID fields, not entire formData
    formData[parentField || ''], formData['parent_id'], formData['parentid'], formData['id'],
    formData[`${parentField}_selectedData`]]);

  useEffect(() => {
    const fetchReportData = async () => {


      if (!settings) {
        setError("Report settings not specified");

        return;
      }

      // Get parent ID from formData if Parent_Field is specified
      const parentId = getParentId(formData, parentField);



      // If Parent_Field is specified but parentId is not available, don't fetch yet
      if (parentField && (!parentId || parentId === "" || parentId === null || parentId === undefined)) {

        setHtmlContent(null);
        setError(null);
        return;
      }



      setLoading(true);
      setError(null);

      try {
        // Use the report endpoint: /pabreport/report?name={settings}
        const endpoint = `/pabreport/report`;

        // Build query parameters for URL (name is still in query params)
        const params: Record<string, any> = {
          name: settings,
        };

        // Prepare payload - use the selected record from lookup (like Blazor's args.SelectedJson.Data)
        let payload: Record<string, any> = {};

        // If parent_id is specified, use the parent record data from lookup rawData
        if (parentId && parentField) {
          // First, try to use the record from lookup rawData (preferred - matches Blazor behavior)
          if (parentRecordData && Object.keys(parentRecordData).length > 0) {
            // Ensure parentRecordData is a plain object, not a complex structure
            payload = { ...parentRecordData };

            // Ensure id and parent_id are present
            if (!payload.id) payload.id = parentId;
            if (!payload.parent_id) payload.parent_id = parentId;
          } else {
            // Fallback: try to fetch the parent record data if not in rawData
            try {
              // For inspection records, use the vehiclein_add endpoint
              const parentRecordResponse = await apiService.get(
                "/pabws/screen/vehiclein_add",
                { id: parentId },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;

                    // Handle different response structures
                    let recordData = null;
                    if (raw.data) {
                      if (Array.isArray(raw.data)) {
                        recordData = raw.data[0] || raw.data;
                      } else {
                        recordData = raw.data;
                      }
                    } else if (Array.isArray(raw)) {
                      recordData = raw[0];
                    } else {
                      recordData = raw;
                    }

                    // Validate that it's not a config structure
                    if (recordData && typeof recordData === "object") {
                      if (recordData.ControlType || recordData.ChildControls || recordData.FormName) {

                        return null;
                      }
                    }

                    return recordData;
                  },
                }
              );

              // Use the fetched parent record data as payload
              if (parentRecordResponse && typeof parentRecordResponse === "object") {
                // Double-check it's not a config structure
                if (
                  !parentRecordResponse.ControlType &&
                  !parentRecordResponse.ChildControls &&
                  !parentRecordResponse.FormName &&
                  // Ensure it has actual data (id or parent_id), not just { success: true }
                  (parentRecordResponse.id || parentRecordResponse.parent_id)
                ) {
                  payload = { ...parentRecordResponse };

                } else {

                  payload = {
                    id: parentId,
                    parent_id: parentId,
                  };
                }
              } else {
                // Fallback: use parent_id if record fetch fails
                payload = {
                  id: parentId,
                  parent_id: parentId,
                };
              }
            } catch (fetchError) {
              // If fetching fails, use parent_id as fallback

              payload = {
                id: parentId,
                parent_id: parentId,
              };
            }
          }
        } else {
          // If no parent_id, create a minimal payload from formData (only record fields, not config)
          // Filter out any config-like structures (ChildControls, ControlType, etc.)
          payload = {};
          if (formData && typeof formData === "object") {
            Object.keys(formData).forEach((key) => {
              const value = formData[key];
              // Skip config-like structures
              if (
                !key.includes("ControlType") &&
                !key.includes("ChildControls") &&
                !key.includes("BindValue") &&
                !key.includes("Label") &&
                !key.includes("FormName") &&
                !key.includes("Actions") &&
                typeof value !== "object" ||
                (typeof value === "object" && value !== null && !Array.isArray(value) && !value.ControlType)
              ) {
                payload[key] = value;
              }
            });
          }

          // If payload is still empty, use a minimal structure
          if (Object.keys(payload).length === 0) {
            payload = {};
          }
        }

        // Validate payload - ensure it's a proper record object, not a config structure
        if (payload && typeof payload === "object") {
          // Check if payload looks like a config structure OR lacks ID (e.g. { success: true })
          if (
            payload.ControlType ||
            payload.ChildControls ||
            payload.FormName ||
            (!payload.id && !payload.parent_id)
          ) {
            // Try to extract actual record data or use minimal payload
            payload = {
              id: parentId || "",
              parent_id: parentId || "",
            };
          }
        }

        // Final validation: ensure payload is a proper record object
        if (payload && typeof payload === "object") {
          // Remove any config-like properties that might have leaked in
          const cleanedPayload: Record<string, any> = {};
          Object.keys(payload).forEach((key) => {
            const value = payload[key];
            // Only include simple values or objects that look like data (not config)
            if (
              value === null ||
              value === undefined ||
              typeof value === "string" ||
              typeof value === "number" ||
              typeof value === "boolean" ||
              (typeof value === "object" &&
                !Array.isArray(value) &&
                !value.ControlType &&
                !value.ChildControls &&
                !value.FormName &&
                !value.Actions)
            ) {
              cleanedPayload[key] = value;
            }
          });
          payload = cleanedPayload;
        }



        // Use POST method with row data in payload body
        const response: {
          success?: boolean;
          data?: string;
          [key: string]: any;
        } | string = await apiService.request<{
          success?: boolean;
          data?: string;
          [key: string]: any;
        } | string>({
          endpoint,
          method: "POST",
          params, // Query parameters in URL (name)
          payload, // Row data in body
          includeAuthToken: true,
          errorHandling: "return-empty",
        });



        // Handle response - data should be an HTML string
        if (response) {
          // Check if response is HTML directly (not wrapped in JSON)
          if (typeof response === "string" && response.includes("<html")) {
            setHtmlContent(response);
          }
          // Check if response is the expected format: { success: true, data: "<html>..." }
          else if (typeof response === "object" && response.success && response.data && typeof response.data === "string") {
            setHtmlContent(response.data);
          }
          // Check if data field contains HTML string
          else if (typeof response === "object" && response.data && typeof response.data === "string" && response.data.includes("<html")) {
            setHtmlContent(response.data);
          }
          else {

            setError("Report data is not in expected format");
            setHtmlContent(null);
          }
        } else {
          setError("Failed to load report data");
          setHtmlContent(null);
        }
      } catch (err: any) {

        setError(err.message || "Failed to load report data");
        setHtmlContent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, parentField, parentRecordData, getParentId,
    // Only re-fetch when the parent ID actually changes, not on every formData change
    formData[parentField || ''], formData['parent_id'], formData['parentid'], formData['id']]);

  // Additional effect to log formData changes for debugging
  useEffect(() => {

  }, [formData, parentField, getParentId]);


  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "#ffffff",
    padding: config.style?.padding !== undefined ? config.style.padding : 0,
    borderRadius: config.style?.borderRadius || 8,
    marginBottom: config.style?.margin || 10,
    border: config.style?.borderWidth
      ? `${config.style.borderWidth}px solid ${config.style.borderColor || "#e5e7eb"}`
      : "px solid #e5e7eb",
    width: "100%",
    gridColumn: "1 / -1", // Span all columns in grid layout

  };

  const titleStyles: React.CSSProperties = {
    fontSize: config.style?.fontSize || 20,
    fontWeight: config.style?.fontWeight || "bold",
    color: config.style?.textColor || "#111827",
    textAlign: config.style?.textAlign || "center",
    marginBottom: 16,
  };

  if (loading) {
    return (
      <div style={containerStyles} className="w-full">

        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading report data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyles} className="w-full">

        <div className="flex items-center justify-center py-8">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!htmlContent) {
    return (
      <div style={containerStyles} className="w-full">
        <div className="p-10 flex flex-col items-center justify-center text-center">
          <div className="bg-primary-50 dark:bg-primary-900/30 rounded-full p-3 mb-3">
            <svg
              className="h-8 w-8 text-primary-600 dark:text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-gray-900 dark:text-gray-100 font-medium text-lg">No Data Available</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-sm">
            No report data found at this time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />
      <div style={containerStyles} className="w-full">


        <div
          className="report-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        />


      </div>
    </>
  );
};

